import { useEffect } from 'react';
import Alert from '@mui/material/Alert';
import { PageContainer } from '../layout/page-container';
import { PageHeader } from '../layout/page-header';
import {
  canGuardDeniedBounce,
  claimDeniedBounce,
  forgetDeniedBounce,
  hasDeniedBounce,
  leaveForHome,
} from './denied-redirect';
import { useFeatures } from './feature-context';

import type { ReactNode } from 'react';

export type RequireFeatureProps = {
  /** A leaf feature the member must hold. Any one of a list is enough. */
  featureGuid?: string | string[];
  /** A feature group the member must hold something in. Checked when no featureGuid is given. */
  groupGuid?: string;
  children: ReactNode;
};

export function RequireFeature({ featureGuid, groupGuid, children }: RequireFeatureProps) {
  const { hasAccess, hasGroupAccess, isLoading } = useFeatures();

  let allowed = true;
  if (featureGuid !== undefined) {
    allowed = hasAccess(featureGuid);
  } else if (groupGuid !== undefined) {
    allowed = hasGroupAccess(groupGuid);
  }
  const denied = !allowed && !isLoading;
  const bounced = hasDeniedBounce();
  const leaving = denied && !bounced && canGuardDeniedBounce();

  useEffect(() => {
    if (leaving) {
      claimDeniedBounce();
      leaveForHome();
    } else if (!isLoading && bounced) {
      forgetDeniedBounce();
    }
  }, [leaving, bounced, isLoading]);

  let content: ReactNode = <>{children}</>;
  if (denied && !leaving) {
    content = (
      <PageContainer>
        <PageHeader title="No access" description="This page is not part of what your role can reach." />
        <Alert severity="info" role="status">
          Ask an administrator at your organization to grant you access if you need this page.
        </Alert>
      </PageContainer>
    );
  } else if (leaving) {
    content = null;
  }
  return content;
}
